import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BottomNavigation, BottomNavigationAction, Paper, Badge, useTheme } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import SettingsIcon from '@mui/icons-material/Settings';
import BarChartIcon from '@mui/icons-material/BarChart';

interface MobileNavigationProps {
  unreadCount?: number;
}

const MOBILE_ROUTES = [
  { labelKey: 'nav.articles', path: '/articles', icon: ArticleIcon },
  { labelKey: 'nav.subscriptions', path: '/subscriptions', icon: RssFeedIcon },
  { labelKey: 'articles.starred', path: '/articles?favorite=true', icon: BookmarkIcon },
  { labelKey: 'nav.stats', path: '/stats', icon: BarChartIcon },
  { labelKey: 'nav.settings', path: '/settings', icon: SettingsIcon },
];

const MobileNavigation: React.FC<MobileNavigationProps> = ({ unreadCount = 0 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();

  const getCurrentValue = () => {
    const pathname = location.pathname;
    const isFavorite = location.search.includes('favorite=true');

    if (pathname === '/articles' && isFavorite) return 2; // Favorite tab

    const idx = MOBILE_ROUTES.findIndex((route) => {
      if (route.path.includes('?')) {
        return false; // Skip routes with query params for path matching
      }
      return pathname === route.path || pathname.startsWith(route.path + '/');
    });

    return idx >= 0 ? idx : 0;
  };

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    const route = MOBILE_ROUTES[newValue];
    if (route.path.includes('?')) {
      // Handle query params separately
      const [path, query] = route.path.split('?');
      navigate(`${path}?${query}`);
    } else {
      navigate(route.path);
    }
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar,
        borderTop: '1px solid var(--glass-border)',
        backgroundColor: 'var(--glass-bg-strong)',
        backdropFilter: 'blur(24px) saturate(180%)',
        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        pb: 'env(safe-area-inset-bottom)',
      }}
      elevation={8}
    >
      <BottomNavigation
        value={getCurrentValue()}
        onChange={handleChange}
        showLabels
        sx={{
          height: 64,
          backgroundColor: 'transparent',
          '& .MuiBottomNavigationAction-root': {
            minWidth: 60,
            maxWidth: 168,
            padding: '6px 12px 8px',
            transition: 'all 0.2s ease',
            '&.Mui-selected': {
              paddingTop: '4px',
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.75rem',
                fontWeight: 600,
              },
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.7rem',
            fontWeight: 500,
            marginTop: '4px',
            '&.Mui-selected': {
              fontSize: '0.75rem',
            },
          },
        }}
      >
        {MOBILE_ROUTES.map((route, index) => {
          const Icon = route.icon;
          const isArticles = index === 0;

          return (
            <BottomNavigationAction
              key={route.path}
              label={t(route.labelKey)}
              icon={
                isArticles && unreadCount > 0 ? (
                  <Badge
                    badgeContent={unreadCount}
                    color="error"
                    max={99}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.65rem',
                        height: 18,
                        minWidth: 18,
                        padding: '0 4px',
                      },
                    }}
                  >
                    <Icon />
                  </Badge>
                ) : (
                  <Icon />
                )
              }
              sx={{
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              }}
            />
          );
        })}
      </BottomNavigation>
    </Paper>
  );
};

export default MobileNavigation;
